import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, PlusCircle } from "lucide-react";
import styles from "@/components/shell/AttentionVisual.module.css";
import { AiBlock } from "@/components/ax/AiBlock";
import { Button } from "@/components/ax/Button";
import { EmptyAxis } from "@/components/ax/EmptyAxis";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { useAttention } from "@/hooks/useAttention";
import { useFirstView } from "@/hooks/useFirstView";
import { useI18n } from "@/i18n";
import { sheetTitle } from "@/lib/sheetStore";

const AttentionSkeleton = () => (
  <div aria-busy="true" data-testid="attention-skeleton" className={styles.skeleton}>
    {[0, 1, 2].map((i) => (
      <div key={i}>
        <div className="h-4 w-2/3 rounded-[8px] bg-ax-border" />
        <div className="mt-3 h-3.5 w-1/2 rounded-[8px] bg-ax-border" />
      </div>
    ))}
  </div>
);

export default function Attention() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const first = useFirstView("attention");
  const { state, pins, sheetRow, retry } = useAttention();
  const today = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  return (
    <div data-testid="attention-page" className={styles.page}>
      <p className={styles.date}>{today}</p>
      <div className={styles.heading}>
        <h1 className={styles.title} data-testid="attention-title">{t("nav.attention")}</h1>
        {(state === "ready" || state === "empty") && <span className={styles.status} data-testid="attention-status">{t(state === "ready" ? "attention.active" : "attention.quiet", { count: pins.length })}</span>}
      </div>
      <p className={styles.lede}>{t("attention.lede")}</p>

      <PrimaryBar>
        <Button
          variant="primary"
          onClick={() => navigate("/keputusan/baru")}
          data-testid="new-decision-button"
          className="w-full md:w-auto md:min-w-[200px]"
        >
          <PlusCircle size={20} strokeWidth={1.5} aria-hidden="true" />
          {t("decision.new")}
        </Button>
      </PrimaryBar>

      {state === "loading" && <AttentionSkeleton />}

      {state === "error" && (
        <div role="alert" data-testid="attention-error" className={`${first ? "enter " : ""}${styles.error}`}>
          <p className="m-0 text-base text-ax-text2">{t("error.read")}</p>
          <Button variant="secondary" onClick={retry} data-testid="attention-retry-button" className="mt-4">
            {t("retry")}
          </Button>
        </div>
      )}

      {state === "ready" && (
        <>
        <div className={styles.queue}>{t("attention.queue", { count: pins.length })}</div>
        <ul data-testid="attention-pins" className={styles.cards}>
          {pins.map((d, i) => (
            <li
              key={d.id}
              className={first && i < 4 ? "enter" : undefined}
              style={first && i < 4 ? { animationDelay: `${i * 40}ms` } : undefined}
            >
              <Link
                to={`/keputusan/${d.id}`}
                data-testid={`pin-card-${d.id}`}
                className={`${styles.card} ${d.reason === "review" ? styles.critical : ""}`}
              >
                <span className={styles.metadata}>
                  <span className={styles.reason}>{t(d.reason === "nextAction" ? "attention.nextAction" : d.reason ? `att.${d.reason}` : "attention.pinned")}</span>
                  <span>
                  {new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", { day: "numeric", month: "short", year: "numeric" }).format(
                    new Date(d.reviewAt ?? d.updatedAt),
                  )}
                  </span>
                </span>
                <span className={styles.cardTitle}>{d.title}</span>
                <span className={styles.cardFooter}>
                  {d.reason && d.reason !== "unselected" && <span className={styles.cardReason}>{d.reason === "nextAction" ? d.nextAction : t(`att.${d.reason}`)}</span>}
                  <span className={styles.open}>{t("attention.reviewCta")}<ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" /></span>
                </span>
              </Link>
              {d.continuity && (
                <div className={styles.continuity}>
                  <AiBlock testId={`attention-continuity-${d.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p
                          className="m-0 font-mono text-[13px] font-medium leading-[18px] text-ax-text"
                          data-testid={`attention-continuity-verdict-${d.id}`}
                        >
                          {t(`continuity.${d.continuity.verdict}`)}
                        </p>
                        <p className="m-0 mt-0.5 text-sm leading-5 text-ax-text2 [overflow-wrap:anywhere]">
                          {d.continuity.line}
                        </p>
                      </div>
                      <Link
                        to={`/keputusan/${d.id}`}
                        data-testid={`attention-continuity-open-${d.id}`}
                        className="inline-flex min-h-[44px] items-center text-sm font-medium text-ax-link hover:underline shrink-0"
                      >
                        {t("open")}
                      </Link>
                    </div>
                  </AiBlock>
                </div>
              )}
            </li>
          ))}
        </ul>
        </>
      )}

      {state === "empty" && (
        <section data-testid="attention-empty" className={`${first ? "enter " : ""}${styles.empty}`}>
          <EmptyAxis play={first} />
          <p>{t("attention.quietBody")}</p>
        </section>
      )}

      {(state === "ready" || state === "empty") && sheetRow && (
        <section data-testid="attention-sheet-row" className={`${first ? "enter " : ""}${styles.sheet}`}>
          <p className={styles.sheetLabel}>{t("attention.todaySheet")}</p>
          <Link
            to={`/lembar/${sheetRow.id}`}
            data-testid={`attention-sheet-${sheetRow.id}`}
            className={styles.sheetLink}
          >
            <span><span className={`line-clamp-2 ${styles.sheetTitle}`}>{sheetTitle(sheetRow)}</span><span className={styles.sheetCaption}>{t("sheet.title")}</span></span>
            <ArrowRight size={18} strokeWidth={1.5} aria-hidden="true" />
          </Link>
        </section>
      )}

    </div>
  );
}
